(() => {
  const root = document.querySelector('[data-floor-fitting]');
  if (!root || root.dataset.fitBound === 'true') return;
  root.dataset.fitBound = 'true';

  const STORAGE_KEY = 'db-floor-fitting-v6';
  const HANDOFF_KEY = 'db-contact-handoff-v1';
  const STEPS = ['space', 'rug', 'preview'];
  const HASH = {
    intro: '#studio',
    space: '#studio-piece',
    rug: '#studio-tapis',
    preview: '#studio-essayage'
  };

  const readJson = (selector, fallback) => {
    const node = root.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const i18n = readJson('[data-fit-i18n]', {});
  const config = readJson('[data-fit-config]', {});
  const rooms = readJson('[data-fit-rooms]', []);
  const moods = readJson('[data-fit-moods]', []);
  const proportions = readJson('[data-fit-proportions]', []);
  const catalog = (readJson('[data-fit-catalog]', []) || []).map((item) => ({
    ...item,
    id: item.handle,
    name: item.title,
    note: [item.family, item.feel].filter(Boolean).join(' · '),
    image: item.image,
    href: item.url,
    round: false,
    moods: moodsFor(item)
  }));
  const factorOf = (value) => {
    const factor = Number(value);
    if (!(factor > 0)) return 0.8;
    return factor > 1 ? factor / 100 : factor;
  };

  const fill = (template, values) =>
    Object.keys(values || {}).reduce(
      (text, key) => String(text || '').split(`[[${key}]]`).join(values[key] == null ? '' : String(values[key])),
      template || ''
    );

  const qs = (selector) => root.querySelector(selector);
  const qsa = (selector) => Array.from(root.querySelectorAll(selector));

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const formatMoney = (cents) => {
    const amount = Math.round(Number(cents || 0) / 100);
    const formatted = String(amount);
    const pattern = config.moneyFormat;
    if (!pattern) return `${formatted} €`;
    return String(pattern)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, formatted)
      .replace(/\{\{\s*amount\s*\}\}/g, formatted);
  };

  function moodsFor(item) {
    const blob = [item.handle, item.title, item.family, item.feel]
      .join(' ')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const list = new Set();
    if (blob.includes('azilal')) {
      list.add('couleur');
      list.add('graphique');
    }
    if (blob.includes('kilim') || blob.includes('taznakht')) {
      list.add('graphique');
      list.add('calme');
    }
    if (blob.includes('mrirt')) {
      list.add('couleur');
      list.add('calme');
    }
    if (blob.includes('beni')) list.add('calme');
    if (/(ligne|losange|geometr|rayure|stripe|graphic)/.test(blob)) list.add('graphique');
    if (/(couleur|color|azilal)/.test(blob)) list.add('couleur');
    if (!list.size) list.add('calme');
    return Array.from(list);
  }

  const roundTen = (value) => Math.round(Number(value) / 10) * 10;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  const sizeFor = (spaceWidth, spaceLength, factor, roomId, round) => {
    const minWidth = roomId === 'couloir' ? 60 : 100;
    const minLength = roomId === 'couloir' ? 160 : 140;
    const width = roundTen(clamp(spaceWidth * factor, minWidth, spaceWidth));
    const length = roundTen(clamp(spaceLength * factor, minLength, spaceLength));
    if (round) {
      const diameter = roundTen(Math.min(width, length));
      return { width: diameter, length: diameter, label: fill(i18n.round, { size: diameter }) };
    }
    return {
      width,
      length,
      label: fill(i18n.size, { width, length })
    };
  };

  const priceFor = (product, width, length) => {
    if (!product) return { cents: null, valid: false, href: product && product.url };
    const area = Number(width) * Number(length);
    let cents = null;
    if (product.refPriceCents > 0 && product.refArea > 0 && area > 0) {
      cents = Math.round((product.refPriceCents * area) / product.refArea);
    }
    const normalized = `${width}x${length}`;
    const match = (product.sizes || []).find((size) =>
      String(size.id || '').replace(/\s+/g, '') === normalized
    );
    if (match) {
      return { cents: match.cents, valid: true, href: match.url || product.url, delay: product.weeks };
    }
    const href = product.url ? `${product.url}${product.url.includes('?') ? '&' : '?'}size=${encodeURIComponent(`${width} × ${length} cm`)}` : config.catalogUrl;
    const valid = cents > 0 && width >= 60 && width <= 800 && length >= 60 && length <= 800;
    return { cents, valid, href, delay: product.weeks };
  };

  const projectId = () => {
    const stamp = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    const token =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replaceAll('-', '').slice(0, 8).toUpperCase()
        : Math.random().toString(36).slice(2, 10).toUpperCase();
    return `DBE-${stamp}-${token}`;
  };

  const emptyMeasures = () =>
    rooms.reduce((map, room) => {
      map[room.id] = { width: '', length: '' };
      return map;
    }, {});

  let step = 'intro';
  let ready = false;
  let id = '';
  let roomId = null;
  let measurements = emptyMeasures();
  let moodId = config.defaultMood || (moods[0] && moods[0].id) || 'calme';
  let modelId = null;
  let returnHref = '';
  let proportionId = config.defaultProportion || 'equilibree';
  let showAll = false;
  let photoUrl = null;
  let photoName = '';
  let photoToken = 0;
  let contact = { firstName: '', email: '', phone: '', country: 'France', postalCode: '', message: '', consent: false };

  const roomById = (value) => rooms.find((room) => room.id === value) || null;
  const moodById = (value) => moods.find((mood) => mood.id === value) || moods[0] || null;
  const proportionById = (value) => proportions.find((item) => item.id === value) || proportions[1] || proportions[0] || null;
  const modelById = (value) => catalog.find((item) => item.id === value) || null;

  const panel = (name) => qs(`[data-fit-panel="${name}"]`);
  const errorNode = (name) => qs(`[data-fit-error="${name}"]`);

  const setError = (name, message) => {
    const node = errorNode(name);
    if (!node) return;
    node.hidden = !message;
    node.textContent = message || '';
  };

  const currentRoom = () => roomById(roomId);
  const currentModel = () => modelById(modelId);
  const currentMood = () => moodById(moodId);
  const currentProportion = () => proportionById(proportionId);
  const currentMeasure = () => (roomId && measurements[roomId]) || { width: '', length: '' };

  const measureValid = () => {
    const room = currentRoom();
    const measure = currentMeasure();
    const width = Number(measure.width);
    const length = Number(measure.length);
    const minWidth = room ? room.minWidth || 140 : 140;
    return Boolean(room && width >= minWidth && width <= 600 && length >= 160 && length <= 800);
  };

  const plannedSize = () => {
    const room = currentRoom();
    const model = currentModel();
    const proportion = currentProportion();
    const measure = currentMeasure();
    if (!room || !model || !measureValid()) {
      return { width: 0, length: 0, label: i18n.toCalculate || '' };
    }
    return sizeFor(Number(measure.width), Number(measure.length), factorOf(proportion.factor), room.id, Boolean(model.round));
  };

  const recommended = () => catalog.filter((item) => item.moods.includes(moodId)).slice(0, 4);

  const visibleModels = () => {
    const picks = recommended();
    const model = currentModel();
    if (showAll) return catalog;
    if (model && !picks.some((item) => item.id === model.id)) return [model, ...picks];
    return picks;
  };

  const setStep = (next, replace) => {
    qs('[data-fit-feedback]').textContent = '';
    const handoff = qs('[data-fit-handoff]');
    if (handoff) handoff.hidden = true;
    step = next;
    const state = { ...(window.history.state || {}), dbFitStep: next };
    if (replace) window.history.replaceState(state, '', HASH[next] || HASH.intro);
    else window.history.pushState(state, '', HASH[next] || HASH.intro);
    render();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    root.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    const heading = root.querySelector('[data-fit-heading="active"], [data-fit-panel]:not([hidden]) [data-fit-heading]');
    if (heading) heading.focus({ preventScroll: true });
  };

  const persist = () => {
    if (!ready) return;
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          version: 2,
          step,
          projectId: id,
          roomId,
          measurementsByRoom: measurements,
          moodId,
          modelId,
          proportionId,
          contact: { ...contact, consent: false }
        })
      );
    } catch (error) {}
  };

  const restore = () => {
    let saved = null;
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) saved = JSON.parse(raw);
    } catch (error) {
      saved = null;
    }
    const params = new URLSearchParams(window.location.search);
    const requested = params.get('modele');
    returnHref = params.get('return') || '';
    const requestedModel = catalog.some((item) => item.id === requested) ? requested : null;
    if (saved && saved.version === 2) {
      id = saved.projectId || projectId();
      roomId = rooms.some((room) => room.id === saved.roomId) ? saved.roomId : null;
      measurements = { ...emptyMeasures(), ...(saved.measurementsByRoom || {}) };
      moodId = moods.some((mood) => mood.id === saved.moodId) ? saved.moodId : moodId;
      modelId = requestedModel || (catalog.some((item) => item.id === saved.modelId) ? saved.modelId : null);
      proportionId = proportions.some((item) => item.id === saved.proportionId) ? saved.proportionId : proportionId;
      contact = { ...contact, ...(saved.contact || {}), consent: false };
    } else {
      id = projectId();
      modelId = requestedModel;
    }
    const hashStep = Object.entries(HASH).find(([, hash]) => hash === window.location.hash);
    let next = (hashStep && hashStep[0]) || 'intro';
    const room = rooms.find((item) => item.id === (saved && saved.roomId));
    const measure = room && saved && saved.measurementsByRoom && saved.measurementsByRoom[room.id];
    const width = Number(measure && measure.width);
    const length = Number(measure && measure.length);
    const minWidth = room ? room.minWidth || 140 : 140;
    const spaceOk = Boolean(room && width >= minWidth && width <= 600 && length >= 160 && length <= 800);
    if ((next === 'preview' && !(spaceOk && modelId)) || (next === 'rug' && !spaceOk)) next = 'space';
    step = next;
    window.history.replaceState({ ...(window.history.state || {}), dbFitStep: next }, '', HASH[next]);
    ready = true;
    render();
  };

  const renderModels = () => {
    const grid = qs('[data-fit-models]');
    const template = qs('[data-fit-model-template]');
    if (!grid || !template) return;
    grid.replaceChildren();
    visibleModels().forEach((item) => {
      const node = template.content.firstElementChild.cloneNode(true);
      const input = node.querySelector('[data-fit-model]');
      const image = node.querySelector('img');
      input.value = item.id;
      input.checked = modelId === item.id;
      if (item.image) image.src = item.image;
      node.querySelector('strong').textContent = item.name;
      node.querySelector('small').textContent = item.note;
      node.querySelector('em').textContent = fill(i18n.fromPrice, { price: formatMoney(item.refPriceCents) });
      input.addEventListener('change', () => {
        modelId = item.id;
        setError('model', '');
        persist();
      });
      grid.appendChild(node);
    });
    qs('[data-fit-model-legend]').textContent = showAll ? i18n.allModels : i18n.recommendations;
    qs('[data-fit-toggle-models]').textContent = showAll ? i18n.seeRecommended : i18n.seeAll;
  };

  const renderPreview = () => {
    const room = currentRoom();
    const model = currentModel();
    const proportion = currentProportion();
    const measure = currentMeasure();
    if (!room || !model) return;
    const size = plannedSize();
    const quote = priceFor(model, size.width, size.length);
    const delayLabel = quote.delay
      ? fill(i18n.receptionValue, { weeks: quote.delay })
      : i18n.delayOnPage;
    qs('[data-fit-result-heading]').textContent = fill(i18n.resultHeading, { room: room.label.toLowerCase() });
    qs('[data-fit-project-id]').textContent = fill(i18n.project, { id });
    qs('[data-fit-preview-room]').textContent = room.label;
    qs('[data-fit-preview-size]').textContent = size.label;
    qs('[data-fit-product-name]').textContent = model.name;
    const link = qs('[data-fit-product-link]');
    link.href = quote.href || model.href || config.catalogUrl;
    qs('[data-fit-summary-room]').textContent = room.label;
    qs('[data-fit-summary-area]').textContent = fill(i18n.area, { width: measure.width, length: measure.length });
    qs('[data-fit-summary-size]').textContent = size.label;
    qs('[data-fit-summary-reference]').textContent = fill(i18n.referenceFor, {
      price: formatMoney(model.refPriceCents),
      size: (model.sizes && model.sizes[0] && model.sizes[0].label) || ''
    });
    qs('[data-fit-summary-price]').textContent = quote.cents == null ? i18n.checkOnPage : formatMoney(quote.cents);
    qs('[data-fit-summary-delay]').textContent = fill(i18n.delayFrance, { delay: delayLabel });
    qs('[data-fit-size-warning]').hidden = Boolean(quote.valid);
    qs('[data-fit-proportion-copy]').textContent = proportion.description || '';
    const photo = qs('[data-fit-room-photo]');
    const plan = qs('[data-fit-plan]');
    const note = qs('[data-fit-plan-note]');
    plan.classList.remove('floor-fitting__plan--dining', 'floor-fitting__plan--bedroom', 'floor-fitting__plan--hallway');
    if (room.id === 'repas') plan.classList.add('floor-fitting__plan--dining');
    if (room.id === 'chambre') plan.classList.add('floor-fitting__plan--bedroom');
    if (room.id === 'couloir') plan.classList.add('floor-fitting__plan--hallway');
    qs('[data-fit-plan-one]').textContent = room.planPrimary || '';
    qs('[data-fit-plan-two]').textContent = room.planSecondary || '';
    if (photoUrl) {
      photo.hidden = false;
      photo.src = photoUrl;
      plan.hidden = true;
      note.hidden = true;
    } else {
      photo.hidden = true;
      photo.removeAttribute('src');
      plan.hidden = false;
      note.hidden = false;
    }
    const rugImage = qs('[data-fit-rug-image]');
    if (model.image) rugImage.src = model.image;
    qs('[data-fit-rug]').classList.toggle('floor-fitting__rug--round', Boolean(model.round));
    const width = Math.max(Number(measure.width), 1);
    const length = Math.max(Number(measure.length), 1);
    const ratio = width / length;
    const stage = qs('[data-fit-stage]');
    stage.style.setProperty('--space-ratio', String(ratio));
    stage.style.setProperty('--stage-width-from-height', `${72 * ratio}cqh`);
    stage.style.setProperty('--rug-width', `${clamp((size.width / width) * 100, 1, 100)}%`);
    stage.style.setProperty('--rug-height', `${clamp((size.length / length) * 100, 1, 100)}%`);
    qsa('[data-fit-proportion]').forEach((input) => {
      const item = proportionById(input.value);
      const label = input.closest('label').querySelector('[data-fit-proportion-size]');
      if (item && label) {
        label.textContent = sizeFor(Number(measure.width), Number(measure.length), factorOf(item.factor), room.id, Boolean(model.round)).label;
      }
    });
    qs('[data-fit-photo-ready]').hidden = !photoUrl;
    qs('[data-fit-upload]').hidden = Boolean(photoUrl);
    qs('[data-fit-photo-review]').hidden = !photoUrl;
    qs('[data-fit-photo-reminder]').hidden = !photoUrl;
    qs('[data-fit-photo-name]').textContent = photoName;
    qs('[data-fit-count]').textContent = fill(i18n.count, { current: contact.message.length });
  };

  const renderSpace = () => {
    const room = currentRoom();
    const measure = currentMeasure();
    const minWidth = room ? room.minWidth || 140 : 140;
    qs('[data-fit-room-label]').textContent = room ? room.label : i18n.chooseRoomFirst;
    qs('[data-fit-measure-help]').textContent = room && room.measureHelp ? room.measureHelp : i18n.chooseRoomFirst;
    qs('[data-fit-measure-range]').textContent = fill(i18n.measureRange, { min: minWidth });
    const widthInput = qs('[data-fit-width]');
    const lengthInput = qs('[data-fit-length]');
    const box = qs('[data-fit-measure]');
    widthInput.min = String(minWidth);
    widthInput.placeholder = (room && room.widthPlaceholder) || i18n.cmPlaceholder || 'En cm';
    lengthInput.placeholder = (room && room.lengthPlaceholder) || i18n.cmPlaceholder || 'En cm';
    widthInput.value = measure.width;
    lengthInput.value = measure.length;
    widthInput.disabled = !room;
    lengthInput.disabled = !room;
    box.setAttribute('aria-disabled', room ? 'false' : 'true');
    qsa('[data-fit-room]').forEach((input) => {
      input.checked = input.value === roomId;
    });
  };

  const render = () => {
    panel('intro').hidden = step !== 'intro';
    panel('wizard').hidden = step === 'intro';
    panel('space').hidden = step !== 'space';
    panel('rug').hidden = step !== 'rug';
    panel('preview').hidden = step !== 'preview';
    const index = Math.max(STEPS.indexOf(step) + 1, 1);
    qs('[data-fit-progress-label]').textContent = fill(i18n.stepOf, { current: index, total: STEPS.length });
    qsa('[data-fit-heading]').forEach((node) => node.removeAttribute('data-fit-heading'));
    const activePanel = panel(step === 'intro' ? 'intro' : step);
    const heading = activePanel && activePanel.querySelector('h2');
    if (heading) heading.setAttribute('data-fit-heading', 'active');
    if (step === 'space') renderSpace();
    if (step === 'rug') {
      qsa('[data-fit-mood]').forEach((input) => {
        input.checked = input.value === moodId;
      });
      renderModels();
    }
    if (step === 'preview') renderPreview();
    persist();
  };

  const validateSpace = () => {
    const room = currentRoom();
    const measure = currentMeasure();
    const width = Number(measure.width);
    const length = Number(measure.length);
    const minWidth = room ? room.minWidth || 140 : 140;
    const errors = {};
    if (!room) errors.room = i18n.chooseRoom;
    else {
      if (!measure.width) errors.width = i18n.widthRequired;
      else if (width < minWidth || width > 600) errors.width = fill(i18n.widthRange, { min: minWidth });
      if (!measure.length) errors.length = i18n.lengthRequired;
      else if (length < 160 || length > 800) errors.length = i18n.lengthRange;
    }
    setError('room', errors.room || '');
    setError('width', errors.width || '');
    setError('length', errors.length || '');
    if (errors.room) qs('[data-fit-room]') && qs('[data-fit-room]').focus();
    else if (errors.width) qs('[data-fit-width]').focus();
    else if (errors.length) qs('[data-fit-length]').focus();
    return !Object.keys(errors).length;
  };

  const summaryLines = () => {
    const room = currentRoom();
    const model = currentModel();
    const mood = currentMood();
    const proportion = currentProportion();
    const measure = currentMeasure();
    const size = plannedSize();
    const quote = model ? priceFor(model, size.width, size.length) : {};
    const delayLabel = quote.delay ? fill(i18n.receptionValue, { weeks: quote.delay }) : i18n.delayOnPage;
    return [
      fill(i18n.summaryProject, { id }),
      fill(i18n.summaryRoom, { room: room ? room.label : '' }),
      fill(i18n.summaryArea, { area: fill(i18n.area, { width: measure.width, length: measure.length }) }),
      fill(i18n.summaryMood, { mood: mood ? mood.label : '' }),
      fill(i18n.summaryRug, { name: model ? model.name : '' }),
      fill(i18n.summaryProportion, { name: proportion ? proportion.label : '' }),
      fill(i18n.summarySize, { size: size.label }),
      fill(i18n.summaryReference, {
        price: model ? formatMoney(model.refPriceCents) : '',
        size: (model && model.sizes && model.sizes[0] && model.sizes[0].label) || ''
      }),
      fill(i18n.summaryPrice, { price: quote.cents == null ? i18n.checkOnPage : formatMoney(quote.cents) }),
      fill(i18n.summaryDelay, { delay: delayLabel }),
      fill(i18n.summaryProduct, { url: quote.href || (model && model.href) || '' }),
      '',
      i18n.summaryNote
    ].join('\n');
  };

  const requestText = () => {
    const lines = [
      i18n.hello,
      '',
      fill(i18n.mailIntro, { name: contact.firstName }),
      fill(i18n.mailEmail, { email: contact.email }),
      contact.phone ? fill(i18n.mailPhone, { phone: contact.phone }) : '',
      fill(i18n.mailCountry, { country: contact.country }),
      contact.postalCode ? fill(i18n.mailPostal, { code: contact.postalCode }) : '',
      '',
      summaryLines(),
      '',
      contact.message ? fill(i18n.mailNotes, { notes: contact.message }) : '',
      photoUrl ? i18n.mailPhoto : i18n.mailNoPhoto,
      '',
      i18n.mailAsk
    ];
    return lines.filter((line, index, list) => line || list[index - 1]).join('\n');
  };

  const compressImage = async (file) => {
    if (typeof createImageBitmap !== 'function') return URL.createObjectURL(file);
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 2200 / Math.max(bitmap.width, bitmap.height));
    if (scale === 1) {
      bitmap.close();
      return URL.createObjectURL(file);
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return URL.createObjectURL(file);
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/webp', 0.84));
    return URL.createObjectURL(blob || file);
  };

  qs('[data-fit-start]').addEventListener('click', () => setStep('space'));
  qs('[data-fit-close]').addEventListener('click', () => setStep('intro'));
  qsa('[data-fit-back]').forEach((button) => {
    button.addEventListener('click', () => {
      const index = STEPS.indexOf(step);
      setStep(index <= 0 ? 'intro' : STEPS[index - 1], true);
    });
  });
  qs('[data-fit-next="rug"]').addEventListener('click', () => {
    if (validateSpace()) setStep('rug');
  });
  qs('[data-fit-next="preview"]').addEventListener('click', () => {
    if (!modelId) {
      setError('model', i18n.chooseModel);
      const input = qs('[data-fit-model]');
      if (input) input.focus();
      return;
    }
    setError('model', '');
    setStep('preview');
  });
  qsa('[data-fit-goto]').forEach((button) => {
    button.addEventListener('click', () => setStep(button.getAttribute('data-fit-goto')));
  });
  qs('[data-fit-restart]').addEventListener('click', () => {
    photoToken += 1;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    qs('[data-fit-file]').value = '';
    id = projectId();
    roomId = null;
    measurements = emptyMeasures();
    moodId = config.defaultMood || moodId;
    modelId = null;
    proportionId = config.defaultProportion || proportionId;
    showAll = false;
    photoUrl = null;
    photoName = '';
    contact = { firstName: '', email: '', phone: '', country: 'France', postalCode: '', message: '', consent: false };
    qsa('[data-fit-contact]').forEach((field) => {
      if (field.type === 'checkbox') field.checked = false;
      else field.value = field.getAttribute('data-fit-contact') === 'country' ? 'France' : '';
    });
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {}
    setStep('space', true);
  });
  qsa('[data-fit-room]').forEach((input) => {
    input.addEventListener('change', () => {
      roomId = input.value;
      setError('room', '');
      renderSpace();
      persist();
    });
  });
  qs('[data-fit-width]').addEventListener('input', (event) => {
    if (!roomId) return;
    measurements[roomId] = { ...currentMeasure(), width: event.target.value };
    setError('width', '');
    persist();
  });
  qs('[data-fit-length]').addEventListener('input', (event) => {
    if (!roomId) return;
    measurements[roomId] = { ...currentMeasure(), length: event.target.value };
    setError('length', '');
    persist();
  });
  qsa('[data-fit-mood]').forEach((input) => {
    input.addEventListener('change', () => {
      moodId = input.value;
      const model = currentModel();
      if (model && !model.moods.includes(moodId)) showAll = true;
      renderModels();
      persist();
    });
  });
  qs('[data-fit-toggle-models]').addEventListener('click', () => {
    showAll = !showAll;
    renderModels();
  });
  qsa('[data-fit-proportion]').forEach((input) => {
    input.addEventListener('change', () => {
      proportionId = input.value;
      renderPreview();
      persist();
    });
  });
  qs('[data-fit-file]').addEventListener('change', async (event) => {
    const file = event.target.files && event.target.files[0];
    const token = photoToken + 1;
    photoToken = token;
    setError('photo', '');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError('photo', i18n.photoType);
      event.target.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('photo', i18n.photoSize);
      event.target.value = '';
      return;
    }
    const status = qs('[data-fit-photo-status]');
    const box = qs('[data-fit-photo]');
    status.hidden = false;
    box.setAttribute('aria-busy', 'true');
    try {
      const url = await compressImage(file);
      if (photoToken !== token) {
        URL.revokeObjectURL(url);
        return;
      }
      if (photoUrl) URL.revokeObjectURL(photoUrl);
      photoUrl = url;
      photoName = file.name;
      renderPreview();
    } catch (error) {
      if (photoToken === token) {
        setError('photo', i18n.photoFail);
        event.target.value = '';
      }
    } finally {
      if (photoToken === token) {
        status.hidden = true;
        box.setAttribute('aria-busy', 'false');
      }
    }
  });
  qs('[data-fit-photo-remove]').addEventListener('click', () => {
    photoToken += 1;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    photoUrl = null;
    photoName = '';
    qs('[data-fit-file]').value = '';
    renderPreview();
  });
  qs('[data-fit-photo-review]').addEventListener('click', () => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    qs('[data-fit-preview]').scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  });
  qsa('[data-fit-contact]').forEach((field) => {
    field.addEventListener('input', () => {
      const key = field.getAttribute('data-fit-contact');
      contact[key] = field.type === 'checkbox' ? field.checked : field.value;
      setError(key, '');
      qs('[data-fit-handoff]').hidden = true;
      if (key === 'message') qs('[data-fit-count]').textContent = fill(i18n.count, { current: field.value.length });
      persist();
    });
    field.addEventListener('change', () => {
      const key = field.getAttribute('data-fit-contact');
      contact[key] = field.type === 'checkbox' ? field.checked : field.value;
      persist();
    });
  });
  qs('[data-fit-form]').addEventListener('submit', (event) => {
    event.preventDefault();
    const errors = {};
    if (contact.firstName.trim().length < 2) errors.firstName = i18n.firstNameError;
    if (!/^\S+@\S+\.\S+$/.test(contact.email.trim())) errors.email = i18n.emailError;
    if (contact.country.trim().length < 2) errors.country = i18n.countryError;
    if (!contact.consent) errors.consent = i18n.consentError;
    ['firstName', 'email', 'country', 'consent'].forEach((key) => setError(key, errors[key] || ''));
    if (Object.keys(errors).length) {
      const first = qs(`[data-fit-contact="${Object.keys(errors)[0]}"]`);
      if (first) first.focus();
      return;
    }
    qs('[data-fit-handoff]').hidden = false;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    qs('[data-fit-handoff]').scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  });
  qs('[data-fit-open-request]').addEventListener('click', () => {
    qs('[data-fit-feedback]').textContent = '';
    try {
      const room = currentRoom();
      const model = currentModel();
      sessionStorage.setItem(
        HANDOFF_KEY,
        JSON.stringify({
          version: 1,
          subject: fill(i18n.subject, {
            id,
            name: model ? model.name : '',
            room: room ? room.label : ''
          }).slice(0, 200),
          message: requestText().slice(0, 5000),
          name: contact.firstName.slice(0, 100),
          email: contact.email.slice(0, 200),
          returnHref: config.pageUrl || '/pages/essayage-au-sol'
        })
      );
      const url = new URL(config.contactUrl || '/pages/contact', window.location.origin);
      url.searchParams.set('source', 'essayage');
      window.location.assign(`${url.pathname}${url.search}`);
    } catch (error) {
      qs('[data-fit-feedback]').textContent = i18n.draftFail;
    }
  });
  qs('[data-fit-copy-request]').addEventListener('click', async () => {
    qs('[data-fit-feedback]').textContent = '';
    try {
      await navigator.clipboard.writeText(requestText());
      qs('[data-fit-feedback]').textContent = i18n.copied;
    } catch (error) {
      qs('[data-fit-feedback]').textContent = i18n.copyFail;
    }
  });
  window.addEventListener('popstate', (event) => {
    const next = event.state && event.state.dbFitStep;
    if (!next || !HASH[next]) return;
    if ((next === 'preview' && !(measureValid() && modelId)) || (next === 'rug' && !measureValid())) {
      step = 'space';
      window.history.replaceState({ ...(window.history.state || {}), dbFitStep: 'space' }, '', HASH.space);
    } else {
      step = next;
    }
    render();
  });
  window.addEventListener('pagehide', () => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
  });

  restore();
})();
